import React from "react";
import {
    Box,
    Text,
    HStack,
    Icon,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    DrawerCloseButton
} from "@chakra-ui/react";
import ContactCard from "../../contactCards/contactCard";

/**
 * Renders an empty state placeholder when no contacts are available.
 * @param {Object} props
 * @param {React.ElementType} props.Icon - react-icons component
 * @param {string} props.emptyText - Message to display
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function EmptyState({ Icon: IconCmp, emptyText, isMobile }) {
    return (
        <Box
            textAlign="center"
            padding={isMobile ? 8 : 12}
            background="rgba(255, 255, 255, 0.02)"
            borderRadius="md"
            border="1px solid rgba(255, 255, 255, 0.05)"
        >
            <Icon as={IconCmp} boxSize={isMobile ? "48px" : "64px"} color="rgba(255, 255, 255, 0.2)" mb={4} />
            <Text color="rgba(255, 255, 255, 0.4)" fontSize={isMobile ? "0.75rem" : "0.8rem"}>
                {emptyText}
            </Text>
        </Box>
    );
}

/**
 * Returns Chakra style props for the contact table header container.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function headerContainerSx(isMobile) {
    return {
        mb: 6,
        padding: isMobile ? "14px 16px" : "16px 20px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.12)"
    };
}

/**
 * Renders the icon badge for the contact table header.
 * @param {Object} props
 * @param {React.ElementType} props.Icon - react-icons component
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function HeaderIconBadge({ Icon: IconCmp, isMobile }) {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={isMobile ? "32px" : "36px"}
            height={isMobile ? "32px" : "36px"}
            borderRadius="8px"
            background="rgba(23, 220, 184, 0.15)"
            backdropFilter="blur(10px)"
            border="1px solid rgba(23, 220, 184, 0.3)"
            boxShadow="0 2px 8px rgba(23, 220, 184, 0.2)"
        >
            <Icon as={IconCmp} boxSize={isMobile ? "18px" : "20px"} color="rgba(23, 220, 184, 1)" />
        </Box>
    );
}

/**
 * Glassmorphism header bar with icon and title for the contact table.
 * @param {Object} props
 * @param {React.ElementType} props.Icon - Header icon component
 * @param {string} props.title - Header title
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function ContactTableHeader({ Icon: IconCmp, title, isMobile }) {
    return (
        <Box {...headerContainerSx(isMobile)}>
            <HStack spacing={3} alignItems="center">
                <HeaderIconBadge Icon={IconCmp} isMobile={isMobile} />
                <Text
                    color="rgba(255, 255, 255, 0.95)"
                    fontSize={isMobile ? "0.9rem" : "1rem"}
                    fontWeight={600}
                    letterSpacing="0.3px"
                >
                    {title}
                </Text>
            </HStack>
        </Box>
    );
}

/**
 * Side drawer that displays full contact card details.
 * @param {Object} props
 * @param {Object|null} props.selectedContact - Currently selected contact for drawer
 * @param {Function} props.onClose - Close handler
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @param {Function} props.getCurrencySymbol - Currency symbol resolver
 * @returns {React.ReactElement}
 */
function ContactDrawer({ selectedContact, onClose, isMobile, getCurrencySymbol }) {
    return (
        <Drawer placement="right" isOpen={!!selectedContact} onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent
                width={isMobile ? "100%" : "500px"}
                maxWidth={isMobile ? "100%" : "500px"}
                background="rgba(30, 30, 30, 0.95)"
                backdropFilter="blur(30px)"
                boxShadow="0 16px 64px rgba(0, 0, 0, 0.3)"
            >
                <DrawerCloseButton color="rgba(255, 255, 255, 0.8)" />
                <DrawerBody p={0}>
                    {selectedContact && (
                        <ContactCard contactCard={selectedContact} currency={getCurrencySymbol(selectedContact)} />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

/**
 * Returns Chakra style props for the footer container.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function footerContainerSx(isMobile) {
    return {
        mt: 5,
        padding: isMobile ? "12px 16px" : "14px 20px",
        background: "rgba(255, 255, 255, 0.06)",
        backdropFilter: "blur(15px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    };
}

/**
 * Renders the count badge for the footer.
 * @param {Object} props
 * @param {number} props.count - Contact count to display
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function FooterCountBadge({ count, isMobile }) {
    return (
        <Box
            padding="4px 12px"
            background="rgba(23, 220, 184, 0.15)"
            backdropFilter="blur(10px)"
            borderRadius="8px"
            border="1px solid rgba(23, 220, 184, 0.3)"
            boxShadow="0 2px 8px rgba(23, 220, 184, 0.2)"
        >
            <Text
                color="rgba(23, 220, 184, 1)"
                fontSize={isMobile ? "0.9rem" : "1rem"}
                fontWeight={700}
                letterSpacing="0.3px"
            >
                {count}
            </Text>
        </Box>
    );
}

/**
 * Footer bar showing a label and the total contact count badge.
 * @param {Object} props
 * @param {string} props.label - Footer label text
 * @param {number} props.count - Contact count
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function ContactTableFooter({ label, count, isMobile }) {
    return (
        <Box {...footerContainerSx(isMobile)}>
            <Text
                color="rgba(255, 255, 255, 0.6)"
                fontSize={isMobile ? "0.7rem" : "0.75rem"}
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="0.5px"
            >
                {label}
            </Text>
            <FooterCountBadge count={count} isMobile={isMobile} />
        </Box>
    );
}

export { EmptyState, ContactTableHeader, ContactDrawer, ContactTableFooter };
